import { useEffect, useState } from "react";
import { AlertTriangle, ArrowDown, ArrowUp, Copy, Pencil, Plus, Trash2, X } from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../common/ToastMsg";
import { ASSIGNMENT_RULES_ORDER_URL, ASSIGNMENT_RULES_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import ConditionBuilder from "./ConditionBuilder";
import Simulator from "./Simulator";
import { Field, Pill, Switch, btnOutline, btnPrimary, inputCls } from "./shared";

/**
 * Advanced mode: ordered rules. The first rule whose WHEN and IF match a lead
 * decides its WHO and HOW; OTHERWISE says what happens when nobody in that
 * pool is free. The default team (Overview tab) answers when no rule matches.
 */
export default function RulesTab({ meta, teams, users, setting, onChanged }) {
    const { fetch, create, update, remove } = useApiMethods();
    const [rules, setRules] = useState(null);
    const [canEdit, setCanEdit] = useState(false);
    const [editing, setEditing] = useState(null);     // rule object (new rule has no id)
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");

    const load = () => fetch(ASSIGNMENT_RULES_URL).then(res => {
        if (res?.status === "success") { setRules(res.rules); setCanEdit(res.can_edit); }
        else ToastMsg("error", res?.message || "Could not load rules");
    });
    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const move = async (idx, dir) => {
        const ids = rules.map(r => r.id);
        const j = idx + dir;
        if (j < 0 || j >= ids.length) return;
        [ids[idx], ids[j]] = [ids[j], ids[idx]];
        const before = rules;
        setRules(ids.map(id => rules.find(r => r.id === id)));
        const res = await update(ASSIGNMENT_RULES_ORDER_URL, { ids });
        if (res?.status === "success") setRules(res.rules);
        else { setRules(before); ToastMsg("error", res?.message || "Could not reorder"); }
    };

    const toggle = async (rule) => {
        const res = await update(`${ASSIGNMENT_RULES_URL}${rule.id}/`, { is_active: !rule.is_active, version: rule.version });
        if (res?.status === "success") { load(); onChanged?.(); }
        else ToastMsg("error", res?.message || "Could not change the rule");
    };

    const del = async (rule) => {
        if (!window.confirm(`Delete the rule "${rule.name}"?\n\nLeads it would have matched go to the next rule or the default team. History keeps its name.`)) return;
        const res = await remove(`${ASSIGNMENT_RULES_URL}${rule.id}/`);
        if (res?.status === "success") { ToastMsg("success", res.message); load(); onChanged?.(); }
        else ToastMsg("error", res?.message || "Could not delete");
    };

    if (!rules) return <p className="text-xs text-gray-400 p-4">Loading…</p>;
    const shown = rules.filter(r =>
        (filter === "all" || (filter === "active" && r.is_active) || (filter === "inactive" && !r.is_active)
            || (filter === "invalid" && r.problem))
        && (!search || r.name.toLowerCase().includes(search.toLowerCase())));
    const defaultTeam = teams.find(t => t.id === setting.default_team_id);

    return (
        <div className="space-y-4">
            {setting.mode !== "advanced" && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 p-3 text-xs text-amber-800 dark:text-amber-200">
                    Rules only run in <b>Advanced</b> mode. Switch the mode on the Overview tab; your rules are kept either way.
                </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                    <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search rules…"
                        className={cn(inputCls, "w-48")} />
                    <select value={filter} onChange={e => setFilter(e.target.value)} className={cn(inputCls, "w-32")}>
                        <option value="all">All</option><option value="active">Active</option>
                        <option value="inactive">Off</option><option value="invalid">Needs fixing</option>
                    </select>
                </div>
                {canEdit && (
                    <button type="button" className={btnPrimary} onClick={() => setEditing(newRule())}>
                        <Plus className="h-3.5 w-3.5" /> Add rule
                    </button>
                )}
            </div>

            <div className="space-y-2">
                {shown.map(rule => {
                    const idx = rules.findIndex(r => r.id === rule.id);
                    return (
                        <div key={rule.id} className={cn("rounded-lg border p-3 flex gap-3",
                            rule.problem ? "border-red-200 dark:border-red-800" : "border-gray-200 dark:border-gray-700",
                            !rule.is_active && "opacity-60")}>
                            <div className="flex flex-col items-center gap-0.5">
                                <span className="text-[10px] font-semibold text-gray-400">{idx + 1}</span>
                                {canEdit && <>
                                    <button type="button" title="Move up" disabled={idx === 0} onClick={() => move(idx, -1)}
                                        className="p-0.5 rounded text-gray-400 hover:text-primary disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
                                    <button type="button" title="Move down" disabled={idx === rules.length - 1} onClick={() => move(idx, 1)}
                                        className="p-0.5 rounded text-gray-400 hover:text-primary disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
                                </>}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{rule.name}</span>
                                    {rule.problem
                                        ? <Pill className="bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300">Needs fixing</Pill>
                                        : !rule.is_active && <Pill>Off</Pill>}
                                    <span className="text-[10px] text-gray-400">matched {rule.matches_30d} in 30 days</span>
                                </div>
                                <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">{rule.sentence}</p>
                                {rule.problem && (
                                    <p className="text-[11px] text-red-600 mt-0.5 flex items-center gap-1">
                                        <AlertTriangle className="h-3 w-3" /> {rule.problem}. Leads skip this rule until it is fixed.
                                    </p>
                                )}
                            </div>
                            {canEdit && (
                                <div className="flex items-start gap-1">
                                    <Switch checked={rule.is_active} onChange={() => toggle(rule)} label="Rule on" />
                                    <button type="button" title="Edit" onClick={() => setEditing(rule)}
                                        className="p-1.5 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"><Pencil className="h-3.5 w-3.5" /></button>
                                    <button type="button" title="Duplicate"
                                        onClick={() => setEditing({ ...rule, id: undefined, version: undefined, name: `${rule.name} (copy)` })}
                                        className="p-1.5 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"><Copy className="h-3.5 w-3.5" /></button>
                                    <button type="button" title="Delete" onClick={() => del(rule)}
                                        className="p-1.5 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"><Trash2 className="h-3.5 w-3.5" /></button>
                                </div>
                            )}
                        </div>
                    );
                })}
                {!rules.length && (
                    <p className="text-xs text-gray-500 py-4">No rules yet. Every lead uses the default team below.</p>
                )}
                <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-3 text-xs text-gray-600 dark:text-gray-300">
                    <b>When no rule matches:</b> {defaultTeam ? `${defaultTeam.name} team, ${setting.default_strategy.replace("_", " ")}` : "no default team set"}
                    {" "}— change it on the Overview tab.
                </div>
            </div>

            <Simulator fields={meta.fields} triggers={meta.triggers} />

            {editing && (
                <RuleEditor rule={editing} meta={meta} teams={teams} users={users}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); onChanged?.(); }}
                    create={create} update={update} />
            )}
        </div>
    );
}

function newRule() {
    return {
        name: "", description: "", triggers: ["*new"],
        conditions: { match: "all", conditions: [] },
        target_type: "team", target_team_id: null, target_user_id: null,
        strategy: "round_robin", respect_daily_cap: true, respect_open_cap: true,
        fallback_action: "next_rule", fallback_user_id: null, is_active: true,
    };
}

function Card({ tag, title, children, tone }) {
    return (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <div className="flex items-center gap-2 mb-2">
                <span className={cn("text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded", tone)}>{tag}</span>
                <span className="text-xs font-semibold text-gray-800 dark:text-gray-100">{title}</span>
            </div>
            {children}
        </div>
    );
}

function RuleEditor({ rule, meta, teams, users, onClose, onSaved, create, update }) {
    const [draft, setDraft] = useState(rule);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [warnings, setWarnings] = useState([]);
    const set = (patch) => setDraft(d => ({ ...d, ...patch }));
    const allNew = (draft.triggers || []).includes("*new");
    const [showWhen, setShowWhen] = useState(!allNew);

    const save = async () => {
        setSaving(true); setErrors({});
        try {
            const body = {
                name: draft.name, description: draft.description, triggers: draft.triggers,
                conditions: draft.conditions, target_type: draft.target_type,
                target_team_id: draft.target_team_id, target_user_id: draft.target_user_id,
                strategy: draft.target_type === "team" ? draft.strategy : "round_robin",
                respect_daily_cap: draft.respect_daily_cap, respect_open_cap: draft.respect_open_cap,
                fallback_action: draft.fallback_action, fallback_user_id: draft.fallback_user_id,
                is_active: draft.is_active,
            };
            const res = draft.id
                ? await update(`${ASSIGNMENT_RULES_URL}${draft.id}/`, { ...body, version: draft.version })
                : await create(ASSIGNMENT_RULES_URL, body);
            if (res?.status === "success") {
                ToastMsg("success", res.message);
                if (res.warnings?.length) { setWarnings(res.warnings); setDraft(res.rule); return; }
                onSaved();
            } else if (res?.code === "version_conflict") {
                ToastMsg("error", res.message);
            } else {
                setErrors(res?.errors || {});
                ToastMsg("error", res?.message || "Could not save the rule");
            }
        } finally { setSaving(false); }
    };

    const fallbacks = (meta.rule_fallbacks || []).filter(f => !(draft.target_type === "user" && f.value === "team_manager"));

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-stretch justify-center p-2 sm:p-4" role="dialog" aria-modal="true">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-6xl flex flex-col max-h-full">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{draft.id ? "Edit rule" : "New rule"}</h2>
                    <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"><X className="h-4 w-4" /></button>
                </div>
                <div className="flex-1 overflow-y-auto grid lg:grid-cols-[1fr_22rem] gap-4 p-4">
                    <div className="space-y-3">
                        <div className="grid sm:grid-cols-2 gap-3">
                            <Field label="Rule name" error={errors.name}>
                                <input value={draft.name} onChange={e => set({ name: e.target.value })} maxLength={80}
                                    className={inputCls} placeholder="e.g. South India – Meta leads" />
                            </Field>
                            <Field label="Description (optional)">
                                <input value={draft.description || ""} onChange={e => set({ description: e.target.value })}
                                    className={inputCls} maxLength={255} />
                            </Field>
                        </div>

                        <Card tag="WHEN" title="Which leads it looks at" tone="bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200">
                            {!showWhen ? (
                                <p className="text-xs text-gray-600 dark:text-gray-300">
                                    Every new lead, from every source.{" "}
                                    <button type="button" className="text-primary hover:underline" onClick={() => setShowWhen(true)}>Limit to some sources</button>
                                </p>
                            ) : (
                                <div className="flex flex-wrap gap-x-4 gap-y-1">
                                    <label className="flex items-center gap-1 text-xs text-gray-700 dark:text-gray-200">
                                        <input type="checkbox" checked={allNew}
                                            onChange={e => set({ triggers: e.target.checked ? ["*new"] : [] })} /> All new leads
                                    </label>
                                    {!allNew && (meta.triggers || []).map(t => (
                                        <label key={t.value} className="flex items-center gap-1 text-xs text-gray-700 dark:text-gray-200">
                                            <input type="checkbox" checked={(draft.triggers || []).includes(t.value)}
                                                onChange={e => set({ triggers: e.target.checked
                                                    ? [...(draft.triggers || []), t.value]
                                                    : draft.triggers.filter(x => x !== t.value) })} /> {t.label}
                                        </label>
                                    ))}
                                </div>
                            )}
                            {errors.triggers && <p className="text-[11px] text-red-600 mt-1">{errors.triggers[0]}</p>}
                        </Card>

                        <Card tag="IF" title="Conditions" tone="bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200">
                            <ConditionBuilder value={draft.conditions} onChange={c => set({ conditions: c })}
                                fields={meta.fields} errors={errors} maxConditions={meta.limits?.max_conditions} />
                        </Card>

                        <Card tag="WHO" title="Give it to" tone="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
                            <div className="flex gap-4 text-xs mb-2">
                                {[["team", "A team"], ["user", "One person"]].map(([k, l]) => (
                                    <label key={k} className="flex items-center gap-1 text-gray-700 dark:text-gray-200">
                                        <input type="radio" checked={draft.target_type === k}
                                            onChange={() => set({ target_type: k,
                                                fallback_action: k === "user" && draft.fallback_action === "team_manager" ? "next_rule" : draft.fallback_action })} /> {l}
                                    </label>
                                ))}
                            </div>
                            {draft.target_type === "team" ? (
                                <Field error={errors.target_team_id}>
                                    <select value={draft.target_team_id || ""} onChange={e => set({ target_team_id: e.target.value ? Number(e.target.value) : null })}
                                        className={cn(inputCls, "max-w-sm")}>
                                        <option value="">Pick a team…</option>
                                        {teams.filter(t => t.is_active).map(t => <option key={t.id} value={t.id}>{t.name} ({t.members})</option>)}
                                    </select>
                                </Field>
                            ) : (
                                <Field error={errors.target_user_id}>
                                    <select value={draft.target_user_id || ""} onChange={e => set({ target_user_id: e.target.value ? Number(e.target.value) : null })}
                                        className={cn(inputCls, "max-w-sm")}>
                                        <option value="">Pick a person…</option>
                                        {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                                    </select>
                                </Field>
                            )}
                            <div className="flex flex-wrap gap-4 mt-2">
                                <label className="flex items-center gap-1 text-xs text-gray-700 dark:text-gray-200">
                                    <input type="checkbox" checked={draft.respect_daily_cap} onChange={e => set({ respect_daily_cap: e.target.checked })} />
                                    Respect daily limits
                                </label>
                                <label className="flex items-center gap-1 text-xs text-gray-700 dark:text-gray-200">
                                    <input type="checkbox" checked={draft.respect_open_cap} onChange={e => set({ respect_open_cap: e.target.checked })} />
                                    Respect open-lead limits
                                </label>
                            </div>
                        </Card>

                        {draft.target_type === "team" && (
                            <Card tag="HOW" title="How to share leads in the team" tone="bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200">
                                <div className="grid sm:grid-cols-3 gap-2">
                                    {[["round_robin", "Round robin", "In turn: A, B, C, A…"],
                                      ["weighted", "Weighted", "By each member's weight"],
                                      ["least_busy", "Least busy", "Fewest open leads first"]].map(([k, l, h]) => (
                                        <label key={k} className={cn("rounded-md border p-2 cursor-pointer text-xs",
                                            draft.strategy === k ? "border-primary bg-brand-50/60 dark:bg-brand-900/20" : "border-gray-200 dark:border-gray-700")}>
                                            <input type="radio" className="mr-1" checked={draft.strategy === k} onChange={() => set({ strategy: k })} />
                                            <b className="text-gray-800 dark:text-gray-100">{l}</b>
                                            <p className="text-[11px] text-gray-500 mt-0.5">{h}</p>
                                        </label>
                                    ))}
                                </div>
                            </Card>
                        )}

                        <Card tag="OTHERWISE" title="If nobody there is free" tone="bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                            <div className="grid sm:grid-cols-2 gap-2">
                                <Field error={errors.fallback_action}>
                                    <select value={draft.fallback_action} onChange={e => set({ fallback_action: e.target.value })} className={inputCls}>
                                        {fallbacks.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                                    </select>
                                </Field>
                                {draft.fallback_action === "user" && (
                                    <Field error={errors.fallback_user_id}>
                                        <select value={draft.fallback_user_id || ""} onChange={e => set({ fallback_user_id: e.target.value ? Number(e.target.value) : null })}
                                            className={inputCls}>
                                            <option value="">Pick a person…</option>
                                            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                                        </select>
                                    </Field>
                                )}
                            </div>
                            <p className="text-[11px] text-gray-500 mt-1">A lead nobody can take waits in Leads → Unassigned and is retried as people free up.</p>
                        </Card>

                        {warnings.map(w => (
                            <p key={w} className="text-xs text-amber-700 bg-amber-50 dark:bg-amber-900/20 rounded p-2 flex items-center gap-1">
                                <AlertTriangle className="h-3.5 w-3.5" /> {w}
                            </p>
                        ))}
                    </div>

                    <div className="lg:border-l lg:pl-4 border-gray-200 dark:border-gray-700">
                        <p className="text-xs font-semibold text-gray-800 dark:text-gray-100 mb-2">Test this rule before saving</p>
                        <Simulator compact fields={meta.fields} triggers={meta.triggers}
                            draftRule={{ ...draft, id: draft.id || undefined }} />
                    </div>
                </div>
                <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                    <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-200">
                        <Switch checked={draft.is_active} onChange={v => set({ is_active: v })} label="Rule on" /> Rule is on
                    </label>
                    <div className="flex gap-2">
                        <button type="button" className={btnOutline} onClick={warnings.length ? onSaved : onClose}>
                            {warnings.length ? "Close" : "Cancel"}
                        </button>
                        <button type="button" className={btnPrimary} onClick={save} disabled={saving}>
                            {saving ? "Saving…" : "Save rule"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
